<script>
	import { onMount } from "svelte";
	import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
	import { point as pointGenerator } from "@turf/helpers";
	import { max, quantile } from "d3-array";
	import { scaleQuantile } from "d3-scale";

	import Search from "./search/Search.svelte";
	import MediaQuery, { breakpoint, platform, orientation, isTouch } from "../../components/MediaQuerySsr.svelte";
	import Map from "./map/Map.svelte";
	import Tooltip from "./components/Tooltip.svelte";
	import Legend from "./components/Legend.svelte";
	import Filters from "./filters/Filters.svelte";
	import AllTopicsView from "./filters/AllTopicsView.svelte";

	import { clickOutside } from "../../actions/clickOutside.js";
	import { hoverOutside } from "../../actions/hoverOutside.js";

	// import { loadMapData, geoStreetSegments, geoStreets, geoBins } from "./data";
	import {
		loadMapData,
		geoBinsSmall,
		geoBinsMedium,
		geoBinsLarge,
		geoBinsXLarge,
		allBerlinStats,
		topics,
	} from "./data";

	import { getColorScale } from "../../colors";
	import { initialBounds, maxBounds, initialPadding, paddingOnZoom } from "./constants";
	import {
		hovered,
		selected,
		searchTerms,
		selectedTopicId,
		selectedTopic,
		showAllTopicsFilterView,
		scrollToTopOfMap,
		scrollToBottomOfMap,
	} from "./stores";
	import ScrollDownButton from "./components/ScrollDownButton.svelte";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;

	// ///////////////////////////////////////////////////////////
	// init, constants and definitions
	// ///////////////////////////////////////////////////////////

	let initialized = false;
	// let isTouch = false;
	let dataLoaded = false;
	let mapLoaded = false;

	onMount(async () => {
		// function checkTouch() {
		// 	isTouch = true;
		// 	document.querySelector("body").removeEventListener("touchstart", checkTouch);
		// }
		// document.querySelector("body").addEventListener("touchstart", checkTouch);

		await loadMapData();
		dataLoaded = true;
	});

	$: showUIComponents = initialized && mapLoaded;

	// ///////////////////////////////////////////////////////////
	// bound to components
	// ///////////////////////////////////////////////////////////
	let stopSearch;

	let map;
	let showLuftbilder;
	let addPopup;
	let removePopup;
	let addMarker;
	let removeMarker;
	let searchHeight = 0;

	// ///////////////////////////////////////////////////////////
	// set up map
	// ///////////////////////////////////////////////////////////
	$: initialBoundsForDevice = Object.keys(initialBounds).includes($platform)
		? initialBounds[$platform]
		: initialBounds["tablet"];
	$: initialPaddingForDevice = Object.keys(initialPadding).includes($platform)
		? initialPadding[$platform]
		: { top: 0, bottom: 0, left: 0, right: 0 };
	$: zoomPaddingForDevide = Object.keys(paddingOnZoom).includes($platform)
		? paddingOnZoom[$platform]
		: initialPaddingForDevice;

	$: showXLargeBins = $platform === "mobile" || $platform === "tablet";
	const zoomThresholdsAll = {
		withXL: {
			"bins-xlarge": { maxzoom: 12 },
			"bins-large": { minzoom: 12, maxzoom: 14 },
			"bins-medium": { minzoom: 14, maxzoom: 15.5 },
			"bins-small": { minzoom: 15.5 },
		},
		normal: {
			"bins-large": { maxzoom: 12 },
			"bins-medium": { minzoom: 12, maxzoom: 15 },
			"bins-small": { minzoom: 15 },
		},
	};
	$: zoomThresholds = zoomThresholdsAll[showXLargeBins ? "withXL" : "normal"];

	$: if (dataLoaded && map && !initialized) {
		const REFLAYER = map.firstLabelLayer;

		// map.addChoropleth($geoBinsSmall, {
		// 	...choroplethOpts,
		// 	id: "bins-small",
		// 	identifier: "id",
		// 	beforeLayer: REFLAYER,
		// 	minzoom: 10,
		// 	zoomPadding: () => {
		// 		return {
		// 			top: 0,
		// 			left: 0,
		// 			bottom: window.innerWidth <= 544 ? 150 : 0,
		// 			right: window.innerWidth <= 544 ? 0 : 290,
		// 		};
		// 	},
		// });

		let mouseoverTimeout;
		const TIMEOUT_INTERVAL = 7.8;

		const commonOpts = {
			clickMinZoom: zoomThresholds[Object.keys(zoomThresholds)[0]].maxzoom - 0.5,
			clickCallback: (layer, f) => {
				removeMarker();
				stopSearch();
				if (f) {
					map.highlightFeature(layer, f, $selected);
					$selected = f;
				} else {
					map.unhighlightFeature(layer, $selected);
					$selected = undefined;
				}
			},
			backgroundClickCallback: (layer) => {
				removeMarker();
				stopSearch();
				if ($selected?.layer?.id === layer) {
					map.unhighlightFeature(layer, $selected);
					$selected = undefined;
				}
			},
			mousemoveCallback: (layer, f) => {
				if ($isTouch) return;
				if (mouseoverTimeout) clearTimeout(mouseoverTimeout);
				mouseoverTimeout = setTimeout(() => {
					if (f) {
						// if ($hovered?.id !== $selected?.id) {
						// 	map.unhighlightFeature(layer, $selected);
						// }
						map.highlightFeature(layer, f, $hovered, $hovered?.id === $selected?.id);
						$hovered = f;
					} else {
						map.unhighlightFeature(layer, $hovered);
						map.highlightFeature(layer, $selected);
						$hovered = undefined;
					}
				}, TIMEOUT_INTERVAL);
			},
			mouseleaveCallback: (layer) => {
				setTimeout(() => {
					// if ($hovered?.id !== $selected?.id) {
					map.unhighlightFeature(layer, $hovered);
					map.highlightFeature(layer, $selected);
					$hovered = undefined;
					// }
				}, TIMEOUT_INTERVAL);
			},
			// TODO: currently hard-coded stops, should generate dynamically
			paintProperties: getPaintProperties(),
			// beforeLayer: map.firstLabelLayer,
			beforeLayer: "place",
			zoomPadding: zoomPaddingForDevide,
		};

		// map.addGeometry($geoPoints, {
		// 	...commonOpts,
		// 	minzoom: 13,
		// 	id: "points",
		// 	type: "point",
		// 	paintProperties: {
		// 		color: getTopicColor(),
		// 	},
		// });

		map.addGeometry($geoBinsSmall, {
			...commonOpts,
			id: "bins-small",
			type: "polygon",
			...zoomThresholds["bins-small"],
		});

		map.addGeometry($geoBinsMedium, {
			...commonOpts,

			id: "bins-medium",
			type: "polygon",
			...zoomThresholds["bins-medium"],
		});

		map.addGeometry($geoBinsLarge, {
			...commonOpts,
			id: "bins-large",
			type: "polygon",
			...zoomThresholds["bins-large"],
		});

		if (showXLargeBins) {
			map.addGeometry($geoBinsXLarge, {
				...commonOpts,
				id: "bins-xlarge",
				type: "polygon",
				...zoomThresholds["bins-xlarge"],
			});
		}

		map.onZoom(() => {
			zoomLevel = map.map.getZoom();
		});

		initialized = true;
	}

	let zoomLevel;
	let activeLayer;

	$: {
		Object.entries(zoomThresholds).forEach(([layerId, { minzoom, maxzoom }]) => {
			if ((!minzoom || zoomLevel > minzoom) && (!maxzoom || zoomLevel < maxzoom)) {
				activeLayer = layerId;
			}
		});
	}

	const checkSelectedIsInActive = () => {
		if ($selected && !searchingZoomInProgress) {
			let selectedBinLayer = $selected.layer.id;
			if (selectedBinLayer !== activeLayer) {
				if ($selected.properties?.searchTerm) {
					const lat = $selected.properties.searchLat;
					const lng = $selected.properties.searchLng;
					executeSearchInMap({ lat, lng, datum: { title: $selected.properties?.searchTerm } });
				} else {
					map.unhighlightFeature(selectedBinLayer, $selected);
					$selected = undefined;
				}
			}
		}
	};

	const checkHoveredIsInActive = () => {
		if ($hovered && !searchingZoomInProgress) {
			let hoveredBinLayer = $hovered.layer.id;
			if (hoveredBinLayer !== activeLayer) {
				map.unhighlightFeature(hoveredBinLayer, $hovered);
				$hovered = undefined;
			}
		}
	};

	// if a bin is selected and user zooms out of that hexbin range, selected and hovered is reset to null
	$: activeLayer, $selected, searchingZoomInProgress, checkSelectedIsInActive();
	$: activeLayer, $hovered, searchingZoomInProgress, checkHoveredIsInActive();

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////
	let tooltipHovered = false;

	$: tooltipData = $hovered || $selected || undefined;
	$: tooltipTitle = Object.keys($searchTerms).includes(String(tooltipData?.id))
		? $searchTerms[tooltipData.id]
		: undefined;

	const tooltipMouseover = () => {
		if (window.innerWidth <= 544) return; // TODO: use $breakpoint
		tooltipHovered = true;
		if ($selected && $hovered) {
			map.unhighlightFeature($hovered.layer.id, $hovered);
			$hovered = undefined;
		}
	};

	const tooltipMouseout = () => {
		if (window.innerWidth <= 544) return; // TODO: use $breakpoint
		tooltipHovered = false;
	};

	// ///////////////////////////////////////////////////////////
	// search
	// ///////////////////////////////////////////////////////////

	let searchingZoomInProgress = false;

	const getLayerGeoData = (layerId) => {
		switch (layerId) {
			case "bins-medium":
				return $geoBinsMedium;

			case "bins-small":
				return $geoBinsSmall;

			case "bins-large":
				return $geoBinsLarge;

			case "bins-xlarge":
				return $geoBinsXLarge;

			default:
				return $geoBinsMedium;
		}
	};

	const findNearestFeature = (lat, lng, layerId = "bins-medium") => {
		let nearestFeature = null;

		// get the right geodata for the layer id
		const geoData = getLayerGeoData(layerId);

		// first try mapbox, only works if feature is visible
		const point = map.map.project([lng, lat]);
		const mapboxFeatures = map.map.queryRenderedFeatures(point, {
			layers: [layerId],
		});

		if (mapboxFeatures.length === 1) {
			nearestFeature = mapboxFeatures[0];
			// if mapbox didn't work, then search in the geometry data
		} else {
			const turfPoint = pointGenerator([lng, lat]);
			for (let bin of geoData.features) {
				if (booleanPointInPolygon(turfPoint, bin)) {
					nearestFeature = { id: bin.properties.id, layer: { id: layerId }, ...bin };
				}
			}
		}
		return nearestFeature;
	};

	const executeSearchInMap = (d, layer = activeLayer) => {
		const nearestFeature = findNearestFeature(d.lat, d.lng, layer);

		if (nearestFeature) {
			map.highlightFeature(layer, nearestFeature, $selected);
			nearestFeature.properties.searchLng = d.lng;
			nearestFeature.properties.searchLat = d.lat;
			nearestFeature.properties.searchTerm = d.datum.title;

			$searchTerms[nearestFeature.id] = nearestFeature.properties.searchTerm;

			$selected = nearestFeature;
		} else {
			map.unhighlightFeature(layer, $selected);
			// if no feature found
			$selected = {
				layer: {
					id: layer,
				},
				properties: {
					searchLng: d.lng,
					searchLat: d.lat,
					searchTerm: d.datum.title,
					demoIds: [],
				},
			};
		}
	};

	$: console.log("search terms", $searchTerms);

	// ///////////////////////////////////////////////////////////
	// filtering
	// ///////////////////////////////////////////////////////////

	const getPaintProperties = (filter = "total") => {
		// get color range for selected filter
		const colorRange = getColorScale(filter);

		const geoData = getLayerGeoData(activeLayer);

		const dataVals = geoData.features.map(({ properties }) => properties[filter]);
		const maxValue = max(dataVals);

		// calculate quantile step - i.e. if 5 colors in color range, quantStep will be 0.2
		const quantStep = 1 / colorRange.length;

		// amount to increment a bin value if a duplicate is found
		const binValIncrement = Math.floor(maxValue / 200) || 0.5;

		let lastBin = 1;
		const binVals = [];

		// generate bins for each color in the colorRange
		for (let i = 0; i < colorRange.length; i++) {
			let binVal = quantile(dataVals, quantStep * i);

			// if a quantile value is the same as the previous, increment it to avoid duplicates
			while (binVal <= lastBin + binValIncrement) {
				binVal++;
			}
			binVals.push(lastBin);
			lastBin = binVal;
			if (binVal > maxValue) {
				break;
			}
		}

		// if there are less bins than colors in the range, shift to ensure bins are colored using the middle colors in the range
		const colorRangeShift = Math.floor((colorRange.length - binVals.length) / 2);
		const stops = binVals.map((val, i) => [val, colorRange[i + colorRangeShift]]);

		return {
			color: {
				property: filter,
				stops,
			},
			opacity: {
				property: filter,
				stops: [
					[1, 0.6],
					[lastBin, 0.8],
				],
				default: 0,
			},
		};
	};

	const setMapColors = () => {
		if (map) {
			const paintProperties = getPaintProperties($selectedTopicId);

			const layers = Object.keys(zoomThresholds);

			layers.forEach((id) => map.setPaintProperties({ id, type: "polygon", paintProperties }));
			// map.setPaintProperties({
			// 	id: "points",
			// 	type: "point",
			// 	paintProperties: { color: getTopicColor($selectedTopicId) },
			// });

			if ($selectedTopicId) {
				layers.forEach((id) => map.setLayerFilter({ id, filter: ["has", $selectedTopicId] }));
			} else {
				layers.forEach((id) => map.setLayerFilter({ id, filter: null }));
			}
		}
	};

	$: $selectedTopicId, setMapColors();
	$: $selectedTopicId && ($showAllTopicsFilterView = false);

	// $: console.log("selected", $selected);
	// $: console.log("hovered", $hovered);
</script>

<MediaQuery />
<div
	class="protests-map"
	class:is-touch={$isTouch}
	on:click|once|stopPropagation|preventDefault={() => {
		if ($platform !== "mobile") return;
		$scrollToTopOfMap();
	}}
>
	<div class="ui-container" bind:clientHeight={searchHeight} class:show-all-topics={$showAllTopicsFilterView}>
		<div class="search">
			<div class="search__headline">Ort oder Gebiet suchen</div>
			<Search
				type={["geo"]}
				icon="pin"
				geoSearchBounds={initialBounds["desktop"]}
				placeholder="z.B. Reinickendorf, 12055, Europaplatz"
				clearCallback={() => {
					map.unhighlightFeature(activeLayer, $selected);
					$selected = undefined;
					removeMarker();
				}}
				resultCallback={(d) => {
					if (d.type === "geo") {
						addMarker(d.lng, d.lat);
						searchingZoomInProgress = true;
						map.map.flyTo({ center: d, zoom: 13, duration: 1000 });
						map.map.on("moveend", () => {
							searchingZoomInProgress = false;
						});
						executeSearchInMap(d, "bins-medium");
					}
				}}
				bind:stopSearch
			/>
		</div>
		<div class="filters">
			<Filters />
		</div>
	</div>

	{#if $showAllTopicsFilterView && $platform !== "mobile"}
		<div class="all-topics-container">
			<AllTopicsView />
		</div>
	{/if}
	{#if showUIComponents}
		<Legend {activeLayer} />
	{/if}

	<div class="map" use:clickOutside on:click_outside={() => {}}>
		{#if $platform === "mobile" || $platform === "tablet"}
			<div class="gradient" />
		{/if}
		<Map
			darkMode={true}
			initialBounds={initialBoundsForDevice}
			{maxBounds}
			padding={initialPaddingForDevice}
			maxZoom={16}
			bind:scrollToTopOfMap={$scrollToTopOfMap}
			bind:scrollToBottomOfMap={$scrollToBottomOfMap}
			bind:map
			bind:showLuftbilder
			bind:addPopup
			bind:removePopup
			bind:addMarker
			bind:removeMarker
			bind:mapLoaded
		>
			<div
				class="tooltip-container"
				slot="tooltip"
				style:height={`calc(100% - ${searchHeight + 15}px)`}
				style:top={`${searchHeight + 10}px`}
			>
				{#if showUIComponents && (tooltipData || $allBerlinStats)}
					{@const hoveredAndSelectedAreSame = $hovered && $selected ? $hovered.id === $selected.id : false}
					{@const tooltipIsSelected = (!$hovered || hoveredAndSelectedAreSame) && !!$selected}
					<Tooltip
						data={tooltipData?.properties || $allBerlinStats}
						title={tooltipTitle}
						isSelected={tooltipIsSelected}
						on:mouseover={tooltipMouseover}
						on:mouseout={tooltipMouseout}
						closeCbk={() => {
							if ($selected) {
								map.unhighlightFeature($selected.layer.id, $selected);
								$selected = undefined;
								stopSearch();
								removeMarker();
							}
						}}
						animateFly={$platform === "mobile"}
					/>
				{/if}
			</div>
		</Map>
		{#if $platform === "desktop" || $platform === "tablet" || !$selected}
			<ScrollDownButton
				on:click={() => {
					map.setInteractivity(false);
					$scrollToBottomOfMap();
				}}
			/>
		{/if}
	</div>
	<div class="attribution">
		Daten:&nbsp;Polizei Berlin via <a href="https://fragdenstaat.de/" target="_blank">FragDenStaat</a>, eigene
		Auswertung. Zeitraum Aug. 2020 - Aug. 2023. Von Demo-Zügen sind nur Startpunkte berücksichtigt.
		<br />
		Kartenbasis:&nbsp;<a href="https://www.openmaptiles.org/" target="_blank">&copy;&nbsp;OpenMapTiles</a>
		<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy;&nbsp;OpenStreetMap contributors</a>
		{#if showLuftbilder}
			| <a
				target="_blank"
				href="https://fbinter.stadt-berlin.de/fb/berlin/service.jsp?id=a_luftbild2022_true_rgbi@senstadt&amp;type=FEED"
				>Geoportal Berlin / Digitale farbige Orthophotos 2022 (DOP20RGBI)</a
			>
		{/if}
	</div>
</div>

<style lang="scss">
	.protests-map {
		user-select: none;
		position: relative;
		font-family: $ff-sans;
		font-size: $fs-base;
		line-height: $lh-base;
		color: $white;
	}

	.map {
		border-radius: 8px;
		overflow: hidden;
		position: relative;
	}

	.gradient {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 260px;
		background-image: linear-gradient(rgba($main-blue, 0.8) 0%, rgba($main-blue, 0.8) 70%, rgba($main-blue, 0) 100%);
		z-index: 1;
		pointer-events: none;
	}

	.attribution {
		font-size: 14px;
		line-height: 1.5em;
		margin-top: 12px;
		text-align: center;

		a {
			text-decoration: none;
		}
	}

	// ///////////////////////////////////////////////////////////
	// UI
	// ///////////////////////////////////////////////////////////
	.ui-container {
		padding: 12px;
		box-sizing: border-box;
		position: absolute;
		top: 5px;
		left: 5px;
		z-index: 5;
		width: 330px;
		border-radius: 5px;
		background: rgba($main-blue, 0.8);
		box-shadow: 0px 0px 15px rgba(255, 255, 255, 0.12);
		backdrop-filter: blur(7.5px);
		-webkit-backdrop-filter: blur(7.5px);
		color: #fff;
		transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out, left 0.3s ease-in-out, top 0.3s ease-in-out;
		max-height: 100vh;

		@media (max-width: $breakpoint-mobile-l) {
			// background: rgba($main-blue, 0.95);

			&.show-all-topics {
				width: 100%;
				top: 0px;
				max-width: unset;
				// left: 0px;
				background: rgba($main-blue, 0.9);
				z-index: 100;

				display: flex;
				flex-direction: column;
				height: 100vh;
				.filters {
					flex-grow: 1;
					overflow: scroll;
				}
			}
		}

		@media (max-width: $breakpoint-tablet-l) {
			left: 50%;
			transform: translateX(-50%);
			width: calc(100% - 10px);
			max-width: 380px;
		}

		.search {
			margin-bottom: 15px;
			text-align: center;

			&__headline {
				color: #fff;
				opacity: 0.5;
				font-size: 12px;
				font-style: normal;
				line-height: normal;
				letter-spacing: 0.96px;
				text-transform: uppercase;
				margin-bottom: 12px;
			}
		}
	}

	.ui-container-bottom {
		display: flex;
		justify-content: space-between;
	}

	.headline {
		font-weight: $bold;
		font-size: 16px;
		text-align: center;
		margin-bottom: 10px;

		@media (min-width: #{$breakpoint-tablet-l + 1px}) {
			display: none;
		}
	}

	.all-topics-container {
		z-index: 100;
		position: relative;

		// @media (min-width: #{$breakpoint-mobile-l + 1px}) {
		// 	top: -150px;
		// }
	}

	.tooltip-container {
		position: absolute;

		@media (max-width: $breakpoint-tablet-l) {
			height: auto !important;
			position: static;
		}
	}

	// ///////////////////////////////////////////////////////////
	// LEGENDE
	// ///////////////////////////////////////////////////////////
	.legend {
		font-size: 13px;
		line-height: 20px;
		white-space: nowrap;
		display: flex;
		flex-direction: row;
		border-radius: 3px;
		text-shadow: -1px 1px 0 $white, 1px 1px 0 $white, 1px -1px 0 $white, -1px -1px 0 $white;
		// @media (min-width: #{$breakpoint-mobile-l + 1px}) {
		// 	&.invert-colors {
		// 		color: $white;
		// 		text-shadow: -1px 1px 0 $black, 1px 1px 0 $black, 1px -1px 0 $black, -1px -1px 0 $black;

		// 		.legend-symbol {
		// 			border: 1px solid $black;
		// 		}
		// 	}
		// }
	}

	.legend-title {
		font-weight: $bold;
	}

	.legend-symbol {
		border-radius: 50%;
	}

	.legend-item {
		display: inline-block;

		&:last-child {
			margin-right: 0;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend-title {
			margin-right: 5px;
		}

		.legend-item {
			margin-right: 5px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.legend-title {
			margin-right: 15px;
		}

		.legend-item {
			margin-right: 10px;
		}
	}

	.legend-symbol {
		display: inline-block;
		width: 8px;
		height: 8px;
		border: 1px solid $white;
		// box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
		margin-right: 2px;
	}

	.legend-label {
		display: inline-block;
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend {
			position: relative;
			background: none;
			padding: 10px 0 10px 0;
			left: 0;
			top: 0;
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
		}
	}

	// ///////////////////////////////////////////////////////////
	// MAP CONTROLS
	// ///////////////////////////////////////////////////////////

	:global(.protests-map .mapboxgl-ctrl-scale) {
		right: 10px;
	}

	:global(.protests-map .tsg-map__controls.tsg-map__controls--vertical) {
		@media (max-width: $breakpoint-mobile-l) {
			top: 40%;
		}

		@media (min-width: #{$breakpoint-tablet-l + 1px}) {
			right: 10px;
		}
	}

	:global(.protests-map .tsg-map-control--activate),
	:global(.protests-map .tsg-map-control--deactivate) {
		display: none !important;
	}

	:global(.dark .mapboxgl-ctrl-scale) {
		text-shadow: 1px 0px 1px $main-blue, -1px 0px 1px $main-blue, 0px 1px 1px $main-blue, 0px -1px 1px $main-blue;
		color: #fff !important;
		border-bottom: 3px $main-blue solid !important;

		&::before {
			border-bottom: 1px solid #fff;
		}
	}

	:global(.dark .tsg-map-control) {
		color: #fff;
		background: $main-blue;
		box-shadow: 0px 0px 10px rgba(#fff, 0.12);

		&::after {
			filter: invert(1);
			opacity: 0.9;
		}
	}

	:global(.dark .tsg-map:not(.tsg-map--is-touch) .tsg-map-control:hover) {
		background: #3e3777;
	}
</style>
